import React, { useState, useEffect } from "react";
import { ethers } from "ethers";
import catCoinAbi from "../abi/CatCoin.json";
import charityAbi from "../abi/CharityWallet.json";

const CATCOIN_ADDRESS = process.env.REACT_APP_TOKEN_CONTRACT;
const CHARITY_ADDRESS = process.env.REACT_APP_CHARITY_CONTRACT;

function ConnectWallet() {
  const [account, setAccount] = useState(null);
  const [balance, setBalance] = useState(null);
  const [chainId, setChainId] = useState(null);
  const [loading, setLoading] = useState(false);

  // --- Exchange UI state ---
  const [showExchange, setShowExchange] = useState(false);
  const [ethAmount, setEthAmount] = useState("");
  const [catAmount, setCatAmount] = useState("");
  const [votingPower, setVotingPower] = useState("0.00");
  const [exchangeRate, setExchangeRate] = useState(null);

  // ✅ connect MetaMask
  const connectWallet = async () => {
    if (!window.ethereum) return alert("⚠️ Please install MetaMask!");
    try {
      const accounts = await window.ethereum.request({
        method: "eth_requestAccounts",
      });
      const provider = new ethers.BrowserProvider(window.ethereum);
      const network = await provider.getNetwork();
      setAccount(accounts[0]);
      setChainId(network.chainId);
      await fetchBalance(accounts[0], provider);
    } catch (err) {
      console.error("❌ Connection failed:", err);
    }
  };

  // ✅ fetch CAT balance
  const fetchBalance = async (address, provider) => {
    try {
      const catCoin = new ethers.Contract(CATCOIN_ADDRESS, catCoinAbi, provider);
      const bal = await catCoin.balanceOf(address);
      setBalance(parseFloat(ethers.formatUnits(bal, 18)).toFixed(4));
    } catch (err) {
      console.error("⚠️ Failed to fetch balance:", err);
      setBalance("0.0000");
    }
  };

  // 🗳 Fetch voting power
  const fetchVotingPower = async (address, provider) => {
    try {
      const catCoin = new ethers.Contract(CATCOIN_ADDRESS, catCoinAbi, provider);
      const votes = await catCoin.getVotes(address);
      const formatted = parseFloat(ethers.formatUnits(votes, 18)).toFixed(2);
      setVotingPower(formatted);
    } catch (err) {
      console.error("⚠️ Failed to fetch voting power:", err);
      setVotingPower("0.00");
    }
  };

  // ✅ register (claim CAT)
  const handleRegister = async () => {
    if (!window.ethereum) return alert("⚠️ Please connect wallet!");
    try {
      setLoading(true);
      const provider = new ethers.BrowserProvider(window.ethereum);
      const signer = await provider.getSigner();
      const catCoin = new ethers.Contract(CATCOIN_ADDRESS, catCoinAbi, signer);

      const tx = await catCoin.claimInitialCAT();
      await tx.wait();
      alert("🎉 Successfully registered and claimed initial CAT!");
      await fetchBalance(await signer.getAddress(), provider);
    } catch (err) {
      console.error("❌ Register failed:", err);
      alert("⚠️ Claim failed or not eligible yet.");
    } finally {
      setLoading(false);
    }
  };

  // 🗳 Delegate voting power to self
  const delegateVotes = async () => {
    if (!window.ethereum) return alert("⚠️ Please connect wallet!");
    try {
      setLoading(true);
      const provider = new ethers.BrowserProvider(window.ethereum);
      const signer = await provider.getSigner();
      const catCoin = new ethers.Contract(CATCOIN_ADDRESS, catCoinAbi, signer);

      const addr = await signer.getAddress();
      const tx = await catCoin.delegate(addr);
      await tx.wait();

      alert("✅ Successfully delegated voting power to yourself!");
      await fetchVotingPower(addr, provider); 
      await fetchBalance(addr, provider);     
    } catch (err) {
      console.error("❌ Delegate failed:", err);
      alert("⚠️ Delegation failed. Please check if your CAT balance is nonzero and try again.");
    } finally {
      setLoading(false);
    }
  };



  // 🧮 Fetch current exchange rate
  const fetchExchangeRate = async () => {
    try {
      const provider = new ethers.BrowserProvider(window.ethereum);
      const charity = new ethers.Contract(CHARITY_ADDRESS, charityAbi, provider);
      const rate = await charity.exchangeRate();

      if (rate && rate > 0n) {
        setExchangeRate(rate);
      } else {
        console.warn("⚠️ exchangeRate returned null or 0, using default");
        setExchangeRate(ethers.parseUnits("0.001", 18));
      }
    } catch (err) {
      console.error("⚠️ Failed to fetch rate:", err);
      setExchangeRate(ethers.parseUnits("0.001", 18));
    }
  };

  // 🧾 Helper to safely format rate
  const formatRate = (rate) => {
    if (!rate || rate === 0n) return "N/A";
    try {
      return parseFloat(ethers.formatUnits(rate, 18)).toLocaleString();
    } catch {
      return "N/A";
    }
  };

  // 🔁 Input handlers
  const handleEthChange = (e) => {
    const eth = e.target.value;
    setEthAmount(eth);
    if (exchangeRate && !isNaN(eth)) {
      const rateInCAT = parseFloat(ethers.formatUnits(exchangeRate, 18));
      setCatAmount((parseFloat(eth) / rateInCAT).toFixed(2));
    }
  };

  const handleCatChange = (e) => {
    const cat = e.target.value;
    setCatAmount(cat);
    if (exchangeRate && !isNaN(cat)) {
      const rateInCAT = parseFloat(ethers.formatUnits(exchangeRate, 18));
      setEthAmount((parseFloat(cat) * rateInCAT).toFixed(6));
    }
  };

  // ✅ confirm exchange
  const confirmExchange = async () => {
    if (!window.ethereum) return alert("⚠️ Please connect wallet!");
    if (!ethAmount || parseFloat(ethAmount) <= 0) return alert("⚠️ Invalid ETH amount");

    try {
      setLoading(true);
      const provider = new ethers.BrowserProvider(window.ethereum);
      const signer = await provider.getSigner();
      const charity = new ethers.Contract(CHARITY_ADDRESS, charityAbi, signer);

      const tx = await charity.exchangeETHForCAT({
        value: ethers.parseEther(ethAmount),
      });
      await tx.wait();
      alert(`💰 Exchanged ${ethAmount} ETH for ${catAmount} CAT successfully!`);
      await fetchBalance(await signer.getAddress(), provider);
      setShowExchange(false);
      setEthAmount("");
      setCatAmount("");
    } catch (err) {
      console.error("❌ Exchange failed:", err);
      alert("⚠️ Exchange failed (maybe below min or paused).");
    } finally {
      setLoading(false);
    }
  };

  // auto detect wallet change
  // 🧭 Auto-detect account + refresh balance & voting power + live Transfer updates
  useEffect(() => {
    if (!window.ethereum) return;
    const provider = new ethers.BrowserProvider(window.ethereum);
    const token = new ethers.Contract(CATCOIN_ADDRESS, catCoinAbi, provider);

    const init = async () => {
      const accs = await provider.listAccounts();
      if (accs.length > 0) {
        const user = accs[0].address || accs[0];
        setAccount(user);
        await fetchBalance(user, provider);
        await fetchVotingPower(user, provider);
      }
    };

    init();

    // 🔄 Listen for account changes
    window.ethereum.on("accountsChanged", async (accounts) => {
      if (accounts.length > 0) {
        const user = accounts[0];
        setAccount(user);
        await fetchBalance(user, provider);
        await fetchVotingPower(user, provider);
      } else {
        setAccount(null);
        setBalance(null);
        setVotingPower("0.00");
      }
    });

    window.ethereum.on("chainChanged", () => window.location.reload());

    // 🪙 Listen to Transfer events → refresh balance
    const updateBalance = async () => {
      if (!account) return;
      await fetchBalance(account, provider);
    };

    token.on(token.filters.Transfer(null, account), updateBalance);
    token.on(token.filters.Transfer(account, null), updateBalance);

    return () => {
      token.removeAllListeners();
      window.ethereum.removeAllListeners("accountsChanged");
      window.ethereum.removeAllListeners("chainChanged");
    };
  }, [account]);


  return (
    <div className="card" style={{ padding: "1.5rem", marginBottom: "1rem" }}>
      <h3>🔌 Wallet Connection</h3>

      {account ? (
        <>
          <p><strong>Connected:</strong> {account}</p>
          <p><strong>Network:</strong> {chainId || "Unknown"}</p>
          <p><strong>Balance:</strong> {balance ? `${balance} CAT` : "Loading..."}</p>
          <p><strong>🗳 Voting Power:</strong> {votingPower} CAT votes</p>

          <div style={{ marginTop: "1rem" }}>
            {/* 🐱 Claim Initial CAT */}
            <button onClick={handleRegister} style={btnStyle} disabled={loading}>
              🐱 Register (Claim CAT)
            </button>

            {/* 💱 Exchange ETH → CAT */}
            <button
              onClick={() => { setShowExchange(true); fetchExchangeRate(); }}
              style={{ ...btnStyle, marginLeft: "10px" }}
              disabled={loading}
            >
              💱 Exchange ETH → CAT
            </button>

            {/* 🗳 Delegate Votes */}
            <button
              onClick={delegateVotes}
              style={{ ...btnStyle, marginLeft: "10px", backgroundColor: "#28a745", color: "#fff" }}
              disabled={loading}
            >
              🗳 Delegate Voting Power
            </button>
          </div>

          {showExchange && (
            <div style={exchangeContainer}>
              <h4 style={{ marginBottom: "0.5rem" }}>💱 Exchange ETH → CAT</h4>
              <p style={{ marginBottom: "1rem" }}>
                Current Rate:&nbsp;
                <strong>
                  {exchangeRate ? `${formatRate(exchangeRate)} ETH per CAT` : "Loading..."}
                </strong>
              </p>

              <div style={inputRow}>
                <div style={inputCol}>
                  <label style={labelStyle}>ETH</label>
                  <input
                    type="number"
                    step="0.0001"
                    value={ethAmount}
                    onChange={handleEthChange}
                    style={inputStyle}
                    placeholder="Enter ETH"
                  />
                </div>
                <div style={arrowStyle}>⇄</div>
                <div style={inputCol}>
                  <label style={labelStyle}>CAT</label>
                  <input
                    type="number"
                    step="0.01"
                    value={catAmount}
                    onChange={handleCatChange}
                    style={inputStyle}
                    placeholder="Enter CAT"
                  />
                </div>
              </div>

              <div style={{ marginTop: "1.5rem", textAlign: "center" }}>
                <button onClick={confirmExchange} style={confirmBtn} disabled={loading}>
                  Confirm Exchange
                </button>
                <button onClick={() => setShowExchange(false)} style={cancelBtn}>
                  Cancel
                </button>
              </div>
            </div>
          )}
        </>
      ) : (
        <button onClick={connectWallet} style={btnStyle}>
          Connect MetaMask
        </button>
      )}
    </div>
  );
}

// 🌈 Styles
const btnStyle = {
  padding: "0.6rem 1.2rem",
  fontSize: "1rem",
  borderRadius: "8px",
  backgroundColor: "#ffcc33",
  border: "none",
  cursor: "pointer",
  fontWeight: "bold",
};

const exchangeContainer = {
  marginTop: "1.5rem",
  padding: "1.5rem",
  border: "1px solid #e5e5e5",
  borderRadius: "12px",
  backgroundColor: "#fffefc",
  boxShadow: "0 2px 8px rgba(0,0,0,0.05)",
  width: "100%",
  maxWidth: "500px",
};

const inputRow = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: "10px",
  flexWrap: "wrap",
};

const inputCol = {
  flex: 1,
  display: "flex",
  flexDirection: "column",
};

const labelStyle = {
  fontSize: "0.9rem",
  marginBottom: "0.3rem",
  color: "#555",
  fontWeight: "500",
};

const inputStyle = {
  padding: "0.6rem 0.8rem",
  fontSize: "1rem",
  borderRadius: "8px",
  border: "1px solid #ccc",
  width: "100%",
  boxSizing: "border-box",
};

const arrowStyle = {
  fontSize: "1.8rem",
  fontWeight: "bold",
  color: "#999",
  textAlign: "center",
  alignSelf: "flex-end",
  paddingBottom: "0.5rem",
};

const confirmBtn = {
  backgroundColor: "#ffcc33",
  color: "#000",
  padding: "0.6rem 1.4rem",
  fontSize: "1rem",
  borderRadius: "8px",
  border: "none",
  cursor: "pointer",
  fontWeight: "bold",
  marginRight: "10px",
};

const cancelBtn = {
  backgroundColor: "#eee",
  color: "#333",
  padding: "0.6rem 1.2rem",
  fontSize: "1rem",
  borderRadius: "8px",
  border: "none",
  cursor: "pointer",
  fontWeight: "bold",
};

export default ConnectWallet;
